// Pure display/link helpers for the affiliate block. Kept out of the component
// module so they unit-test without pulling in Chakra/Redux/React.

// Where a shared referral link points. One referral identity across bot and
// website: the code is the inviter's account id (users.id), the same value the
// Telegram deep link carries. Capturing `?ref=` at web sign-up is Phase 4; here
// we only display the link for the user to copy.
export const REFERRAL_LINK_BASE = "https://ipscore.app/?ref=";

// Never show a negative referral figure (US1 MUST). The API already floors
// these at 0; this is defensive on the client too. Non-numeric → "0.00".
export const formatUsd = (value: unknown): string => {
  const n = Number(value);
  return (Number.isFinite(n) ? Math.max(0, n) : 0).toFixed(2);
};

// Build the shareable referral link from the account id. Returns "" when the id
// is not a positive number (not loaded yet / invalid) so the UI can hide or
// disable the copy control.
export const buildReferralLink = (userId: unknown): string => {
  const id = Number(userId);
  return Number.isFinite(id) && id > 0 ? `${REFERRAL_LINK_BASE}${id}` : "";
};

// ---------------------------------------------------------------------------
// Transfer / payout thresholds — DISPLAY-ONLY mirrors of the server values.
// The server enforces them and stays the source of truth; these exist so the
// UI can block obviously-doomed submits and word the message.
// ---------------------------------------------------------------------------
export const TRANSFER_MIN_USD = 1;
export const PAYOUT_MIN_USD = 50;
export const PAYOUT_FEE_PCT = 0.05;
export const PAYOUT_FEE_MIN_USD = 10;

const round2 = (n: number): number => Math.round(n * 100) / 100;

// Pre-submit payout preview: fee = max(5% of gross, $10), net = gross - fee.
// Convenience math only — the server's payout_request response is the
// authoritative gross/fee/net and is what gets displayed after submit.
export const computePayoutPreview = (
  gross: unknown
): { gross: number; fee: number; net: number } => {
  const n = Number(gross);
  const g = Number.isFinite(n) && n > 0 ? round2(n) : 0;
  if (g <= 0) {
    return { gross: 0, fee: 0, net: 0 };
  }
  const fee = Math.max(round2(g * PAYOUT_FEE_PCT), PAYOUT_FEE_MIN_USD);
  const net = Math.max(round2(g - fee), 0);
  return { gross: g, fee, net };
};

// ---------------------------------------------------------------------------
// ?ref capture (Phase 4). The referral code is the inviter's account id — a
// positive integer. Anything else is ignored (spec: invalid ref → no-op).
// ---------------------------------------------------------------------------
export const REF_STORAGE_KEY = "ref_id";

// Parse a raw candidate (query value / localStorage value) into a positive
// integer referral id, or null when it isn't one. Strict digits-only: "12.5",
// "12abc", "", negative and non-string garbage are all rejected.
export const parseRefId = (value: unknown): number | null => {
  if (typeof value !== "string" && typeof value !== "number") return null;
  const s = String(value).trim();
  if (!/^\d+$/.test(s)) return null;
  const id = Number(s);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

// Extract the referral id from a location.search string ("?ref=123&x=y").
export const extractRefIdFromSearch = (search: unknown): number | null => {
  if (typeof search !== "string" || !search) return null;
  try {
    return parseRefId(new URLSearchParams(search).get("ref") ?? "");
  } catch {
    return null;
  }
};

// Surface a server rejection (FastAPI HTTPException detail) from an axios
// error. Pydantic validation errors ship detail as an array — fall back to a
// generic message rather than rendering "[object Object]".
export const serverErrorMessage = (
  error: unknown,
  fallback = "Something went wrong. Please try later."
): string => {
  const detail = (error as any)?.response?.data?.detail;
  return typeof detail === "string" && detail ? detail : fallback;
};
